import { chain } from '@leprecoins/effector';
import { RouteParams, RouteInstance, chainRoute } from 'atomic-router';
import { attach, createEvent, sample } from 'effector';
import { condition } from 'patronum';
import { UserRole, authModel } from 'shared/api';
import { navigate, replace } from 'shared/lib';
import { sharedModel } from 'shared/model';

export type ChainUnauthorizedParams<TRouteParams extends RouteParams> = {
	route: RouteInstance<TRouteParams>;
	authorizedRedirects: Record<UserRole, RouteInstance<RouteParams>>;
};
export const chainUnauthorized = <TRouteParams extends RouteParams>({
	route,
	authorizedRedirects,
}: ChainUnauthorizedParams<TRouteParams>) => {
	const checkAuthorization = createEvent();

	const alreadyAuthorized = createEvent();

	const navigateAuthorized = attach({
		source: authModel.$role,
		effect: (role) => replace(authorizedRedirects[role!])(),
	}).prepend<void>(() => {});

	condition({
		source: checkAuthorization,
		if: authModel.$authorized,
		then: alreadyAuthorized,
		else: authModel.refresh,
	});

	sample({
		clock: [alreadyAuthorized, authModel.authorized],
		filter: route.$isOpened,
		target: navigateAuthorized,
	});

	return chainRoute({
		route,
		beforeOpen: checkAuthorization,
		openOn: authModel.unauthorized,
	});
};

export const createChainUnauthorized =
	({ authorizedRedirects }: Pick<ChainUnauthorizedParams<RouteParams>, 'authorizedRedirects'>) =>
	<TRouteParams extends RouteParams>(route: RouteInstance<TRouteParams>) =>
		chainUnauthorized({
			route,
			authorizedRedirects,
		});

export type ChainAuthorizedParams<TRouteParams extends RouteParams> = {
	route: RouteInstance<TRouteParams>;
	unauthorizedRedirect: RouteInstance<RouteParams>;
};

export const chainAuthorized = <TRouteParams extends RouteParams>({
	route,
	unauthorizedRedirect,
}: ChainAuthorizedParams<TRouteParams>) => {
	const checkAuthorization = createEvent();

	const alreadyAuthorized = createEvent();

	const navigateUnauthorized = navigate(unauthorizedRedirect);

	condition({
		source: checkAuthorization,
		if: authModel.$authorized,
		then: alreadyAuthorized,
		else: authModel.refresh,
	});

	sample({
		clock: authModel.unauthorized,
		filter: route.$isOpened,
		target: navigateUnauthorized,
	});

	return chainRoute({
		route,
		beforeOpen: checkAuthorization,
		openOn: [alreadyAuthorized, authModel.authorized],
	});
};

export const createChainAuthorized =
	({ unauthorizedRedirect }: Pick<ChainAuthorizedParams<RouteParams>, 'unauthorizedRedirect'>) =>
	<TRouteParams extends RouteParams>(route: RouteInstance<TRouteParams>) =>
		chainAuthorized({
			route,
			unauthorizedRedirect,
		});

export type ChainRolesParams<TRouteParams extends RouteParams> = {
	route: RouteInstance<TRouteParams>;
	roles: UserRole[];
	forbiddenRedirects: Record<UserRole, RouteInstance<RouteParams>>;
};

export const chainRoles = <TRouteParams extends RouteParams>({
	route,
	roles,
	forbiddenRedirects,
}: ChainRolesParams<TRouteParams>) => {
	const checkRole = createEvent();

	const rollPassed = createEvent();

	const navigateForbidden = attach({
		source: authModel.$role,
		effect: (role) => replace(forbiddenRedirects[role!])(),
	}).prepend<void>(() => {});

	const $roleAllowed = authModel.$role.map((role) => role !== null && roles.includes(role));

	condition({
		source: checkRole,
		if: $roleAllowed,
		then: rollPassed,
		else: navigateForbidden,
	});

	return chainRoute({
		route,
		beforeOpen: checkRole,
		openOn: rollPassed,
	});
};

export const createChainRoles =
	({ roles, forbiddenRedirects }: Omit<ChainRolesParams<RouteParams>, 'route'>) =>
	<TRouteParams extends RouteParams>(route: RouteInstance<TRouteParams>) =>
		chainRoles({
			route,
			roles,
			forbiddenRedirects,
		});

export const composeChains =
	(...chains: ((route: RouteInstance<RouteParams>) => RouteInstance<RouteParams>)[]) =>
	<TRouteParams extends RouteParams>(
		route: RouteInstance<TRouteParams>,
	): RouteInstance<TRouteParams> => {
		const chainedRoute = chains.reduce(
			(acc, cur) => (r) => cur(acc(r)),
			(r) => r,
		)(route as RouteInstance<RouteParams>);

		chain(chainedRoute.open, sharedModel.routeLoadingStarted);

		chain(chainedRoute.opened, sharedModel.routeLoadingFinished);

		return chainedRoute as RouteInstance<TRouteParams>;
	};
